import { Button, Tooltip } from '@mantine/core';
import { CreditCard, Mail, MapPin, Phone, ShieldCheck, Smartphone } from 'lucide-react';
import { FC, useState } from 'react';

import NullComponent from '@/components/NullComponent.tsx';

import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { GenericPatient, PhoneNumberType } from '@/models/GenericPatient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { getInsuranceTypeDisplay } from '@/utils/insurance-utils.ts';
import { formatPatientAddress, getPreferredPhoneNumberByType } from '@/utils/patientUtils.ts';

import PatientInformationEditModal from './PatientInformationEditModal.tsx';
import PatientInformationItem from './PatientInformationItem.tsx';

interface Props {
    patient?: GenericPatient;
}

const PatientInformation: FC<Props> = ({ patient }) => {
    const [show_edit_modal, setShowEditModal] = useState(false);

    const { data: enriched_prac_data, is_loading: is_prac_data_loading } =
        useEnrichedPractitionerDataForSpecificInstance(patient?.instance_id ?? '');

    const is_edit_disabled = !patient || patient.pvs_provider === PVSProvider.DATA_CUR;

    const getInsurances = (): string => {
        if (is_prac_data_loading || !patient) return '-';

        const enriched_insurances = patient?.insurances
            ?.map((insurance) => {
                const cost_center: string | undefined = enriched_prac_data?.practices?.find(
                    (p) => p.pvs?.id === insurance.location_id
                )?.pvs?.name;

                return {
                    insurance,
                    cost_center,
                };
            })
            .filter((item) => item.cost_center !== undefined)
            .sort((a, b) => {
                return a.cost_center!.localeCompare(b.cost_center ?? '');
            });

        const insurance_items: string[] =
            enriched_insurances?.map(
                ({ insurance, cost_center }) =>
                    `${cost_center} - ${insurance.name} (${getInsuranceTypeDisplay(insurance.type)})`
            ) ?? [];

        return insurance_items.join(', ') || '-';
    };

    const insurances = getInsurances();

    const handleEditClick = (): void => {
        if (patient && !is_edit_disabled) {
            setShowEditModal(true);
        }
    };

    if (!patient) return <NullComponent />;

    return (
        <div className="flex w-full flex-col gap-4">
            <div className="flex flex-col gap-4">
                <PatientInformationItem
                    icon={ShieldCheck}
                    label="PVS-Zustimmung"
                    value={patient?.is_data_admission_to_pvs_approved ? 'Unterschrieben' : 'Fehlt'}
                />
                <PatientInformationItem icon={CreditCard} label="Krankenversicherung" value={insurances} />
                <PatientInformationItem
                    icon={Smartphone}
                    label="Mobilnummer"
                    value={
                        getPreferredPhoneNumberByType(patient?.contact.phone_numbers, PhoneNumberType.MOBILE) ?? '--'
                    }
                />
                <PatientInformationItem
                    icon={Phone}
                    label="Telefonnummer"
                    value={
                        getPreferredPhoneNumberByType(patient?.contact.phone_numbers, PhoneNumberType.LANDLINE) ?? '--'
                    }
                />
                <PatientInformationItem
                    icon={MapPin}
                    label="Adresse"
                    value={formatPatientAddress(patient?.address) ?? '--'}
                />
                <PatientInformationItem
                    icon={Mail}
                    label="E-Mail"
                    value={patient?.contact.emails.find((email) => email.is_preferred)?.value}
                />
            </div>
            <div className="w-full text-right">
                <Tooltip
                    label={is_edit_disabled ? 'Diese Funktion ist für DataCur Patienten nicht verfügbar' : ''}
                    disabled={!is_edit_disabled}
                >
                    <Button
                        variant="transparent"
                        className="p-0 capitalize underline"
                        onClick={handleEditClick}
                        disabled={is_edit_disabled}
                    >
                        Bearbeiten
                    </Button>
                </Tooltip>
            </div>
            {patient && show_edit_modal && (
                <PatientInformationEditModal
                    opened={show_edit_modal}
                    onClose={() => {
                        setShowEditModal(false);
                    }}
                    patient={patient}
                />
            )}
        </div>
    );
};

export default PatientInformation;
